import type { ComponentType } from 'react';
import type { ThemeData } from '@/types/theme';

// ─── Auto-registration ──────────────────────────────────────────────────────
// Every .tsx file in src/components/sections/ is automatically registered as
// a WordPress-mountable component. No edits to this file are needed.
//
// Convention:
//   File:   src/components/sections/my-feature.tsx
//   Export: export function MyFeature(props) { ... }
//   Slug:   data-component="my-feature"  (used in PHP templates and blocks)
//
// The component receives ALL WordPress site data (siteName, menuItems, logo,
// etc.) automatically via window.gscsThemeData, plus any data-props you pass.

type SectionModule = Record<string, ComponentType<Record<string, unknown>>>;

const sectionModules = import.meta.glob<SectionModule>('./components/sections/*.tsx', {
	eager: true,
});

function slugToName(slug: string): string {
	return slug.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

export const COMPONENT_MAP: Record<string, ComponentType<Record<string, unknown>>> = {};

for (const [path, mod] of Object.entries(sectionModules)) {
	const slug = path.replace('./components/sections/', '').replace('.tsx', '');
	const name = slugToName(slug);
	const Component = mod[name] as ComponentType<Record<string, unknown>> | undefined;
	if (Component) {
		COMPONENT_MAP[slug] = Component;
	}
}

// ─── Mount helper ───────────────────────────────────────────────────────────

function parseProps(serialized?: string): Record<string, unknown> {
	if (!serialized) return {};
	try {
		return JSON.parse(serialized) as Record<string, unknown>;
	} catch {
		return {};
	}
}

export function getMountedComponent(node: HTMLElement) {
	const componentName = node.dataset.component;
	const Component = componentName ? COMPONENT_MAP[componentName] : null;

	if (!Component) return null;

	return {
		Component,
		props: {
			...((window.gscsThemeData || {}) as ThemeData),
			...parseProps(node.dataset.props),
		},
	};
}
